import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFile, FaUser, FaCalendar, FaTag, FaEye, FaArrowLeft } from "react-icons/fa";
import api from "../utils/api";
import ViewResearchModal from "../components/ViewResearchModal";
import CiteThis from "../components/CiteThis";

export default function ResearchList() {
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedResearch, setSelectedResearch] = useState(null);
  /** which card ids show full abstract */
  const [abstractExpanded, setAbstractExpanded] = useState({});

  const ABSTRACT_PREVIEW_CHARS = 150;

  useEffect(() => {
    loadResearch();
  }, []);

  const loadResearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/research/public");
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.research ?? [];
      setResearch(list);
    } catch (err) {
      console.error("Error loading research:", err);
      if (!err.response) {
        setError(
          "Cannot reach the API server. Ensure the backend is running and REACT_APP_API_URL is correct for production."
        );
      } else if (err.response.status === 503) {
        setError(
          err.response.data?.error ||
            "Database is not connected on the server. Check MONGO_URI and MongoDB Atlas settings."
        );
      } else {
        setError(
          err.response.data?.error ||
            err.response.data?.hint ||
            "Failed to load research papers. Please try again later."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (researchItem) => {
    setSelectedResearch(researchItem);
    setShowViewModal(true);
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedResearch(null);
  };

  const filteredResearch = research.filter((item) => {
    return (
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.authors?.some(author => author.toLowerCase().includes(searchTerm.toLowerCase())) ||
      item.abstract?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const toggleAbstractExpanded = (id) => {
    setAbstractExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderAbstractBlock = (item) => {
    if (!item.abstract) {
      return <p>No abstract available.</p>;
    }
    const full = item.abstract;
    const long = full.length > ABSTRACT_PREVIEW_CHARS;
    const expanded = Boolean(abstractExpanded[item._id]);
    return (
      <>
        <p className="whitespace-pre-wrap break-words">
          {long && !expanded ? `${full.slice(0, ABSTRACT_PREVIEW_CHARS)}…` : full}
        </p>
        {long && (
          <button
            type="button"
            onClick={() => toggleAbstractExpanded(item._id)}
            className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
          >
            {expanded ? "View less" : "View more"}
          </button>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading research papers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadResearch}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link 
                to="/" 
                className="flex items-center text-gray-600 hover:text-blue-600 transition-colors mr-8"
              >
                <FaArrowLeft className="mr-2" />
                Back to Home
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">
                <span className="font-extrabold">SKSU Digital Research Hub</span> &amp; Open Access Journal
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title, authors, or abstract..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Access Control Notice */}
        <div className="mb-6 p-4 bg-blue-100 border border-blue-300 rounded">
          <p className="text-blue-800 text-sm">
            <strong>Access Control:</strong> All research documents require you to <span className="font-semibold">log in</span> and submit a request for access. Admin approval is required before you can view any document.
          </p>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredResearch.length} of {research.length} research papers
          </p>
        </div>

        {/* Research List */}
        {filteredResearch.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaFile className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No research papers found</h3>
            <p className="text-gray-600">
              {searchTerm ? "Try adjusting your search criteria." : "No research papers are currently available."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResearch.map((item) => (
              <div key={item._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <FaFile className="h-8 w-8 text-blue-600 flex-shrink-0" />
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {item.status}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaUser className="mr-2" />
                      <span>{item.authors?.join(", ") || "Unknown Author"}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <FaCalendar className="mr-2" />
                      <span>{item.year}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <FaTag className="mr-2" />
                      <span>{item.department}</span>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Campus:</span> {item.campus}
                    </div>
                  </div>
                  
                  <div className="text-gray-600 text-sm mb-4">
                    {renderAbstractBlock(item)}
                  </div>

                  <div className="mb-4">
                    <CiteThis research={item} theme="light" />
                  </div>
                  
                  <Link
                    to="/login"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FaEye className="mr-2" />
                    Login to Request Access
                  </Link>
                  {/* Total Views and Downloads */}
                  <div className="flex items-center gap-6 mt-4 text-gray-600 text-xs justify-center">
                    <span className="flex items-center"><FaEye className="mr-1" /> Total Views: {item.viewCount || 0}</span>
                    <span className="flex items-center"><FaFile className="mr-1" /> Total Downloads: {item.downloadCount || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Research Modal */}
      <ViewResearchModal
        isOpen={showViewModal}
        onClose={handleCloseModal}
        research={selectedResearch}
      />
    </div>
  );
} 